import { useState } from 'react';
import { adminModulesSchema, brandingSchema } from '@teachpartner/contracts';
import { describeError } from '../../api/client';
import { useServices } from '../../app-context';
import { ErrorBanner } from '../../components/badges';

type Kind = 'branding' | 'modules';

const PLACEHOLDER: Record<Kind, string> = {
  branding: '{ "name": "…", "shortName": "…", "theme": { "light": { … }, "dark": { … } } }',
  modules: '{ "modules": [ { "id": "announcements", "enabled": true, … } ] }',
};

/** Host Admin "via ops": pushes branding/modules to the school; audited as host_admin:<email>. */
export function OpsSection({ code, enabled }: { code: string; enabled: boolean }) {
  const { api } = useServices();
  const [kind, setKind] = useState<Kind>('branding');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const send = async () => {
    setError(null);
    setResult(null);
    try {
      const json: unknown = JSON.parse(text);
      const res =
        kind === 'branding'
          ? await api.opsBranding(code, brandingSchema.parse(json))
          : await api.opsModules(code, adminModulesSchema.parse(json));
      setResult(
        res.ok ? 'Applied on the school server.' : `School answered HTTP ${res.tenantStatusCode}.`,
      );
    } catch (e) {
      setError(e instanceof SyntaxError ? 'Not valid JSON.' : describeError(e));
    }
  };

  return (
    <section aria-label="Ops">
      <h2>Ops (via tailnet)</h2>
      {!enabled && <p>Available once the school is enrolled and has a Tailscale address.</p>}
      <ErrorBanner message={error} />
      {result && <div className="banner banner-info">{result}</div>}
      <label>
        Action
        <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
          <option value="branding">Set branding</option>
          <option value="modules">Set modules</option>
        </select>
      </label>
      <label>
        Payload (JSON)
        <textarea
          rows={6}
          value={text}
          placeholder={PLACEHOLDER[kind]}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <button type="button" disabled={!enabled || text.trim() === ''} onClick={() => void send()}>
        Send to school
      </button>
    </section>
  );
}
